// Runtime join for the historical player team-by-season lookup (#22).
//
// Both platforms expose only a player's CURRENT NFL team (Yahoo
// editorial_team_abbr, Sleeper's live /players/nfl `team`), so old-season
// lineups/trades/drafts render the wrong team. historicalTeam() resolves the
// player's actual team for a given season from the static index built by
// src/tools/genHistoricalTeams.js.
//
// A miss returns null and the caller keeps the platform's current-state value,
// so this is strictly additive — never worse than before the lookup.

import data from "../data/historicalTeams.json";
import { normName, posGroup } from "./playerNameNorm.js";

const byNamePos = data.byNamePos || {};

// Lazily-built name-only fallback index: normName -> [full keys]. Used when a
// platform position doesn't map to a posGroup (or mismatches nflverse's) but
// the name is unambiguous in the dataset.
let nameIndex = null;
function ensureNameIndex() {
  if (nameIndex) return;
  nameIndex = new Map();
  for (const key of Object.keys(byNamePos)) {
    const nm = key.slice(0, key.lastIndexOf("|"));
    const arr = nameIndex.get(nm);
    if (arr) arr.push(key);
    else nameIndex.set(nm, [key]);
  }
}

/**
 * Resolve a player's NFL team abbreviation for `season`.
 * @returns team abbrev string, or null when unknown (caller falls back).
 */
export function historicalTeam(name, pos, season) {
  if (!name || season == null) return null;
  const yr = String(season);
  const nm = normName(name);
  if (!nm) return null;

  // 1) exact name + position group
  const pg = posGroup(pos);
  if (pg) {
    const e = byNamePos[`${nm}|${pg}`];
    if (e && e[yr]) return e[yr];
  }

  // 2) name-only fallback, only when the name maps to a single entry (safe;
  //    avoids cross-position misidentification like QB vs OL same-name).
  ensureNameIndex();
  const keys = nameIndex.get(nm);
  if (keys && keys.length === 1) {
    const e = byNamePos[keys[0]];
    if (e && e[yr]) return e[yr];
  }
  return null;
}
